import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { Iztrolabe } from 'react-iztro';
import { styled } from '@mui/material/styles';
import { theme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';

interface BirthData {
  birthday: string;
  birthTime: number;
  originalBirthTime?: string;
  gender: '男' | '女';
  birthdayType: 'solar' | 'lunar';
}

interface AstrolabeChartProps {
  birthData: BirthData;
  style?: React.CSSProperties;
}

// Styled components for animations
const AnimatedContainer = styled(Box)(() => ({
  animation: 'fadeInUp 0.8s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const StyledAstrolabeContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)',
  overflow: 'visible',
  position: 'relative',
  minHeight: 'auto',
  height: 'auto',
  // 手机端特殊处理
  [theme.breakpoints.down('sm')]: {
    margin: '0 -4px',
    maxWidth: 'calc(100vw - 8px)',
    padding: '0.25rem',
    borderRadius: '0.25rem',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '0.375rem',
    borderRadius: '0.375rem',
  },
  [theme.breakpoints.up('md')]: {
    padding: '1rem',
    borderRadius: '0.5rem',
  },
  [theme.breakpoints.up('lg')]: {
    padding: '1.25rem',
    borderRadius: '0.75rem',
  },
  [theme.breakpoints.up('xl')]: {
    padding: '1.5rem',
    borderRadius: '1rem',
  },
  // 响应式字体大小CSS变量覆盖 - 基于容器大小自适应，防止文字溢出
  '& .iztro-astrolabe': {
    [theme.breakpoints.down('sm')]: {
      '--iztro-star-font-size-big': 'clamp(5px, 1.5vw, 8px)',
      '--iztro-star-font-size-small': 'clamp(4px, 1.2vw, 6px)',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      '--iztro-star-font-size-big': 'clamp(6px, 1.7vw, 9px)',
      '--iztro-star-font-size-small': 'clamp(5px, 1.4vw, 7px)',
    },
    [theme.breakpoints.between('md', 'lg')]: {
      '--iztro-star-font-size-big': 'clamp(7px, 1.8vw, 10px)',
      '--iztro-star-font-size-small': 'clamp(6px, 1.5vw, 8px)',
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      '--iztro-star-font-size-big': 'clamp(8px, 1.9vw, 11px)',
      '--iztro-star-font-size-small': 'clamp(7px, 1.6vw, 9px)',
    },
    [theme.breakpoints.up('xl')]: {
      '--iztro-star-font-size-big': 'clamp(9px, 2vw, 12px)',
      '--iztro-star-font-size-small': 'clamp(8px, 1.7vw, 10px)',
    },
  },
  // 运限调节按钮样式覆盖 - 手机端优化
  '& .horo-buttons': {
    [theme.breakpoints.down('sm')]: {
      margin: '4px 8px',
      justifyContent: 'center',
      gap: '2px',
      flexWrap: 'wrap',
    },
    [theme.breakpoints.up('sm')]: {
      margin: '8px 12px',
      justifyContent: 'space-around',
    },
  },
  '& .horo-buttons .center-button': {
    [theme.breakpoints.down('sm')]: {
      padding: '2px 4px',
      fontSize: 'clamp(8px, 2vw, 10px)',
      minWidth: '24px',
      minHeight: '20px',
      border: '1px solid var(--iztro-color-border)',
      borderRadius: '2px',
      margin: '1px',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '4px 6px',
      fontSize: 'var(--iztro-star-font-size-small)',
    },
  },
  '& .center-horo-hour': {
    [theme.breakpoints.down('sm')]: {
      gap: '1px !important',
    },
  },
  // 隐藏 "Powered by iztro" 链接
  '& .iztro-copyright': {
    display: 'none !important',
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    margin: '0.25rem 0',
  },
  [theme.breakpoints.up('xs')]: {
    margin: '0.25rem',
    borderRadius: '0.25rem',
  },
  [theme.breakpoints.up('sm')]: {
    margin: '0.75rem',
    borderRadius: '0.375rem',
  },
  [theme.breakpoints.up('md')]: {
    margin: '1rem',
    borderRadius: '0.5rem',
  },
  [theme.breakpoints.up('lg')]: {
    margin: '1.25rem',
    borderRadius: '0.75rem',
  },
  [theme.breakpoints.up('xl')]: {
    margin: '1.5rem',
    borderRadius: '1rem',
  },
}));

const AstrolabeChart: React.FC<AstrolabeChartProps> = ({ 
  birthData, 
  style 
}) => {
  const [astrolabeWidth, setAstrolabeWidth] = useState(400);
  const [astrolabeHeight, setAstrolabeHeight] = useState(400);

  useEffect(() => {
    const calculateDimensions = () => {
      // 根据屏幕大小动态调整最大尺寸
      let maxSize;
      if (window.innerWidth >= 1920) {
        maxSize = 800; // 大屏幕
      } else if (window.innerWidth >= 1200) {
        maxSize = 700; // 桌面端
      } else if (window.innerWidth >= 768) {
        maxSize = 600; // 平板端
      } else {
        maxSize = Math.min(window.innerWidth * 0.9, 400); // 手机端
      }
      
      const containerWidth = Math.min(window.innerWidth * 0.85, maxSize);
      // 给高度更多空间，避免垂直方向太挤
      const containerHeight = Math.min(window.innerHeight * 0.9, maxSize * 1.2);
      setAstrolabeWidth(containerWidth);
      setAstrolabeHeight(containerHeight);
    };

    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    
    return () => window.removeEventListener('resize', calculateDimensions);
  }, []);

  if (!birthData) {
    return (
      <Box 
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          ...style
        }}
      >
        <Typography 
          variant="h6" 
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          暂无出生数据
        </Typography>
      </Box>
    );
  }



  return (
    <Box 
      sx={{
        width: '100%',
        backgroundColor: 'background.default',
        overflow: 'auto',
        ...style
      }}
    >
      <AnimatedContainer sx={{ p: 2, textAlign: 'center' }}>
        {/* Use react-iztro Astrolabe component */}
        <StyledAstrolabeContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              margin: '0 auto',
              maxWidth: '100%',
              overflow: 'visible',
            }}
          >
            <Iztrolabe
              width={astrolabeWidth}
              birthday={birthData.birthday}
              birthTime={birthData.birthTime}
              gender={birthData.gender}
              birthdayType={birthData.birthdayType}
              centerPalaceAlign={true}
            />
          </Box>
        </StyledAstrolabeContainer>
      </AnimatedContainer>
      
      {/* Birth Information Card */}
      <InfoCard>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontSize: responsiveFontSizes.h6,
              fontWeight: 'bold'
            }}
          >
            出生信息
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: '0.25rem', sm: '0.375rem', md: '0.5rem', lg: '0.625rem', xl: '0.75rem' }
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              出生日期：{birthData.birthday}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              出生时间：{birthData.originalBirthTime || `${birthData.birthTime}时`}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              性别：{birthData.gender}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: responsiveFontSizes.body2 }}
            >
              历法：{birthData.birthdayType === 'solar' ? '公历' : '农历'}
            </Typography>
          </Box>
        </CardContent>
      </InfoCard>
    </Box>
  );
};

export default AstrolabeChart;